import React, { useState, useEffect } from 'react';
import { Plus, Heart, AlertCircle, Calendar, TrendingUp } from 'lucide-react';
import { ConditionSearch } from './conditions/ConditionSearch';
import { MedicalCondition } from '../types/medical';
import ComingSoonBanner from './common/ComingSoonBanner';
import { useMedicalConditions } from '../hooks/useSupabaseData';
import { useConditionPersistence } from '../hooks/useUserDataPersistence';
import MockDataBanner from './common/MockDataBanner';
import { useMockDataDetection } from '../hooks/useMockDataDetection';

interface Condition {
  id: string;
  name: string;
  type: 'chronic' | 'acute' | 'managed';
  severity: 'low' | 'medium' | 'high';
  diagnosedDate: string;
  lastUpdated: string;
  status: 'active' | 'monitoring' | 'resolved';
  icd10Code?: string;
  category?: string;
}

export const ConditionManagement: React.FC = () => {
  const { conditions, addCondition, loading, error } = useMedicalConditions();
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);

  const { saveCondition, getConditions, isAuthenticated, error: persistenceError } = useConditionPersistence({
    autoSync: true,
    validateOnSave: true,
    encrypt: true
  });

  const mockDataInfo = useMockDataDetection('conditions');

  // Listen for events from Quick Start Guide
  useEffect(() => {
    const handleShowAddCondition = () => {
      setShowAddCondition(true);
    };

    window.addEventListener('showAddCondition', handleShowAddCondition);
    
    return () => {
      window.removeEventListener('showAddCondition', handleShowAddCondition);
    };
  }, []);

  const handleConditionSelect = async (medicalCondition: MedicalCondition) => {
    try {
      const newCondition = {
        name: medicalCondition.name,
        category: medicalCondition.category,
        icd10_code: medicalCondition.icd10Code,
        severity: 'medium' as const,
        diagnosed_date: new Date().toISOString().split('T')[0],
        status: 'active' as const,
        notes: `Added from medical conditions database: ${medicalCondition.description || ''}`
      };

      const result = await addCondition(newCondition);
      if (result) {
        setShowAddCondition(false);
        setAddSuccess(`Successfully added ${medicalCondition.name} to your conditions`);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setAddSuccess(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to add condition:', error);
      setAddSuccess('Failed to add condition. Please try again.');
    }
  };

  const handleSaveCondition = async (conditionData: any) => {
    if (!isAuthenticated) {
      setError('Please log in to save conditions');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const dataId = await saveCondition(conditionData);
      
      if (dataId) {
        // Refresh conditions list
        const updatedConditions = await getConditions();
        setConditions(updatedConditions);
        
        // Show success message
        setSuccessMessage('Condition saved successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Reset form
        setSelectedCondition(null);
        setCustomConditionName('');
        setSeverity('moderate');
        setNotes('');
      } else {
        setError('Failed to save condition');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save condition');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = () => {
    // Trigger onboarding flow
    window.dispatchEvent(new CustomEvent('start-onboarding'));
  };

  const handleAddData = () => {
    // Show add condition form
    window.dispatchEvent(new CustomEvent('showAddCondition'));
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-red-600 bg-red-50';
      case 'monitoring': return 'text-blue-600 bg-blue-50';
      case 'resolved': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Mock Data Banner */}
      {mockDataInfo.shouldShowBanner && (
        <MockDataBanner
          dataType="health-conditions"
          onStartOnboarding={handleStartOnboarding}
          onAddData={handleAddData}
          variant="info"
        />
      )}

      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Health Conditions Management"
        description="Your personal conditions dashboard will sync with healthcare providers, track real symptoms, and provide AI insights to help manage your health conditions."
        currentPage="/conditions"
        variant="compact"
        expectedRelease="very soon"
      />
      
      {/* Success Message */}
      {addSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{addSuccess}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Health Conditions</h2>
          <p className="text-slate-600 mt-1">Manage your health conditions and track their progress</p>
        </div>
        <button 
          onClick={() => setShowAddCondition(!showAddCondition)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Condition</span>
        </button>
      </div>

      {/* Add Condition Interface */}
      {showAddCondition && (
        <ConditionSearch 
          onConditionSelect={handleConditionSelect}
          selectedConditions={conditions.map(c => c.id)}
        />
      )}

      {/* Loading and Error States */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-700">Loading your health conditions...</p>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading conditions. Please try again.</p>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-2">
            <Heart className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium text-slate-600">Active Conditions</span>
          </div>
          <p className="text-2xl font-bold text-slate-800 mt-1">{conditions.filter(c => c.status === 'active').length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            <span className="text-sm font-medium text-slate-600">High Severity</span>
          </div>
          <p className="text-2xl font-bold text-slate-800 mt-1">{conditions.filter(c => c.severity === 'high').length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium text-slate-600">This Month</span>
          </div>
          <p className="text-2xl font-bold text-slate-800 mt-1">{conditions.filter(c => {
            const created = new Date(c.created_at);
            const now = new Date();
            return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
          }).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium text-slate-600">Improving</span>
          </div>
          <p className="text-2xl font-bold text-slate-800 mt-1">{conditions.filter(c => c.status === 'monitoring').length}</p>
        </div>
      </div>

      {/* Conditions List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Your Conditions</h3>
        </div>
        <div className="divide-y divide-slate-200">
          {conditions.length === 0 ? (
            <div className="p-6 text-center">
              <Heart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No conditions added yet. Click "Add Condition" to get started.</p>
            </div>
          ) : (
            conditions.map((condition) => (
              <div key={condition.id} className="p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-medium text-slate-800">{condition.name}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(condition.status)}`}>
                        {condition.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(condition.severity)}`}>
                        {condition.severity} severity
                      </span>
                      {condition.category && (
                        <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full">
                          {condition.category}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-slate-600">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>Diagnosed: {condition.diagnosed_date ? new Date(condition.diagnosed_date).toLocaleDateString() : 'Not specified'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <TrendingUp className="h-4 w-4" />
                        <span>Updated: {new Date(condition.updated_at || condition.created_at).toLocaleDateString()}</span>
                      </div>
                      {condition.icd10_code && (
                        <div className="flex items-center space-x-1">
                          <span>ICD-10: {condition.icd10_code}</span>
                        </div>
                      )}
                    </div>
                    {condition.notes && (
                      <p className="text-sm text-slate-600 mt-2">{condition.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      View Details
                    </button>
                    <button className="px-3 py-1 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
